import {
    Tag,
    useDataTable,
    useLoading,
    useSearch, useTranslation,
} from '@components';
import type { IJob, IJobHasContractor, ISecurity, IUserIdentity } from '@models';

import DateUtils from '@utils/date-utils';
import * as React from 'react';

export interface IProps {
    jobId: number;
    workId: number;
    identity: IUserIdentity;
    job: IJob;
    getContractorFromAppUser: Function;
    getJobContractors: Function;
    deleteSubcontractorRequest: Function;
    restrictInvitation: Function;
    security: ISecurity;
}

export function JobContractorSubRequests(props: IProps) {
    const { t } = useTranslation();
    const loading = useLoading();

    const userIsSubcontractor = React.useRef(false);

    const jobHasContractor = React.useRef<IJobHasContractor>();
    const [canRender, setCanRender] = React.useState<boolean>(false);

    const initialize = React.useCallback(async () => {
        const contractorUserData = await props.getContractorFromAppUser(props.workId);

        const jobContractorsData = await props.getJobContractors(props.workId, props.jobId);

        const subContractors = await jobContractorsData.filter((x: IJobHasContractor) => x.parentId !== null);

        const isSubcontractor = await subContractors.some((jc: IJobHasContractor) => jc.contractorId === (contractorUserData?.id ?? 0));

        const jobContractorData = await jobContractorsData.find((jc: IJobHasContractor) => jc.contractorId === (contractorUserData?.id ?? 0));
        userIsSubcontractor.current = isSubcontractor && props.security.isContractor();
        jobHasContractor.current = jobContractorData;

        setCanRender(props.security.isContractor()
            ? jobHasContractor.current != null
            : true);
    }, [props.getContractorFromAppUser, props.workId, props.getJobContractors, props.security.isContractor, props.jobId]);

    React.useEffect(() => {
        initialize();
    }, [initialize]);

  const date = (r: any) => {
    const d = DateUtils.formatFromUtc(r.dateTime!);
    return <div title={d}>
      <span>{d}</span>
      </div>
  };


    const requestSearch = useSearch({
        workId: props.workId,
        search: 'jobs/subcontractor.requests',
        filters: {
            jobId: props.jobId,
            workId: props.workId,
            contractorId: userIsSubcontractor ? (jobHasContractor.current?.parentId) : null,
        }
    });

    const invitationSearch = useSearch({
        workId: props.workId,
        search: 'jobs/subcontractor.invitations.contractors',
        filters: {
            jobId: props.jobId,
            workId: props.workId,
            jobHasContractorId: userIsSubcontractor ? (jobHasContractor.current?.parentId) : null,
        }
    });

    const relationsSearch = useSearch({
        workId: props.workId,
        search: 'jobs/subcontractor.relations',
        filters: {
            jobId: props.jobId,
            workId: props.workId,
        },
        lazy: true,
    });

    const [data, setData] = React.useState<any>([]);

    const renderStatus = (d: any) => {
        if (d.isRelation === true) {
            return <Tag>{t('Subcontractor')}</Tag>;
        }
        if (d.acceptedByContractor || d.accepted) {
            return <Tag severity={'success'}>{t('job.contractor-request.accepted-by-contractor')}</Tag>;
        }
        else if (d.acceptedByWork) {
            return <Tag severity={'warning'}>{t('job.contractor-request.accepted-by-work')}</Tag>;
        }
        else if (d.accepted == null && d.jobId == null) {
            return <Tag>{`${t('Pending')} (${t('Invitation')})`}</Tag>;
        }
        else if ((d.rejected) || (!d.accepted && d.accepted != null)) {
            return <Tag severity={'danger'}>{t('Rejected')}</Tag>;
        }
        else {
            return <Tag>{t('Pending')}</Tag>
        }
    }

    const filteredInvitations = React.useCallback(() => {
        const parentIds: any[] = [];
        parentIds.push(jobHasContractor.current?.id ?? undefined);
        invitationSearch.value.map((x: any) => parentIds.includes(x.parentId) ? parentIds.push(x.id) : undefined);
        requestSearch.value.map((x: any) => parentIds.includes(x.parentId) ? parentIds.push(x.id) : undefined);
        relationsSearch.value.map((x: any) => parentIds.includes(x.parentId) ? parentIds.push(x.id) : undefined);

        if (requestSearch.isCompleted && invitationSearch.isCompleted && (props.workId !== 28 || relationsSearch.isCompleted)) {
            const filteredRequests = requestSearch
                .value
                .filter((i: any) =>
                    jobHasContractor.current
                        ? i.sourceContractorId === jobHasContractor.current?.contractor.id
                        || parentIds.includes(i.parentId)
                        || jobHasContractor.current?.contractorId === i.targetContractorId
                        : true);
            const filteredInvitations = invitationSearch
                .value
                .filter((i: any) =>
                    jobHasContractor.current
                        ? i.sourceId === jobHasContractor.current?.contractor.id
                        || parentIds.includes(i.parentId)
                        : true);
            const filteredRelations = props.workId === 28
                ? relationsSearch
                    .value
                    .filter((i: any) =>
                        jobHasContractor.current
                            ? i.sourceContractorId === jobHasContractor.current?.contractor.id
                            || parentIds.includes(i.parentId)
                            || jobHasContractor.current?.contractorId === i.targetContractorId
                            : true)
                : [];
            const concated: any[] = filteredRequests
                .concat(filteredInvitations)
                .concat(filteredRelations)
                .sort((a: any, b: any) => (b.dateTime ?? '').localeCompare(a.dateTime ?? ''));

            const matchedRequestKeys = new Set<string>();
            concated.forEach((x: any) => {
                if (x.isRelation !== true && x.jobHasContractorId != null && x.targetContractorId != null && x.jobId != null) {
                    matchedRequestKeys.add(`${x.jobId}|${x.targetContractorId}|${x.jobHasContractorId}`);
                }
            });

            const withoutMatchedRelations = concated.filter((x: any) => {
                if (x.isRelation === true && x.parentId != null) {
                    return !matchedRequestKeys.has(`${x.jobId}|${x.targetContractorId}|${x.parentId}`);
                }
                return true;
            });

            const seen = new Set<string>();
            return withoutMatchedRelations.filter((x: any) => {
                const key = `${x.isRelation === true ? 'rel' : (x.token !== undefined ? 'inv' : 'req')}:${x.id}`;
                if (seen.has(key)) {
                    return false;
                }
                seen.add(key);
                return true;
            });
        }
        else {
            return [];
        }
    }, [requestSearch.value, invitationSearch.value, relationsSearch.value, requestSearch.isCompleted, invitationSearch.isCompleted, relationsSearch.isCompleted, props.workId]);


  const refresh = async () => {
    await initialize();
    requestSearch.doSearch();
    invitationSearch.doSearch();
    if (props.workId === 28) {
      relationsSearch.doSearch();
    }
    setData(filteredInvitations().filter(s => !s.blacklisted).filter(s => !s.isDeleted));
  }

  const deleteSubcontractorRequest = (r: any) => {
    props.deleteSubcontractorRequest(props.workId, r.id);
    refresh();
  }

  const restrictContractorInvitation = (r: any) => {
    props.restrictInvitation(r.token);
    refresh();
  }

    React.useEffect(() => {
      if (props.workId === 28) {
        relationsSearch.doSearch();
      }
    }, [props.workId]);

    React.useEffect(() => {
      setData(filteredInvitations().filter(s => !s.blacklisted).filter(s => !s.isDeleted));
    }, [filteredInvitations]);

  const checkRemove = (r: any) => {
    if (!props.security.isGestor()) {
      return true;
    }
    if (r.accepted || r.acceptedByContractor || r.acceptedByWork) {
      return true;
    }
    return false;
  }

    const dataTable = useDataTable({
        columns: [
          { title: t('Date'), className: 'td-datetime-sm', field: 'dateTime', render: (r) => date(r) },
          { title: t('job.contractor-request.source'), render: (d: any) => jobHasContractor ? d.sourceId == jobHasContractor.current?.id || d.sourceContractorId == jobHasContractor.current?.contractorId ? <span title={d.sourceName}><b>{d.sourceName}</b></span> : d.sourceName : <span title={d.sourceName}>d.sourceName</span>},
          { title: t('job.contractor-request.target'), render: (d: any) => jobHasContractor ? d.targetContractorId == jobHasContractor.current?.id ? <span title={d.targetName}><b>{d.targetName}</b></span> : d.targetName : <span title={d.targetName}>d.targetName</span>},
            {
                title: t('Job'),
                render: (d: any) => <span className='r g-10 v-center' title={`${d.jobCode}|${d.jobName}`}>
                    <Tag value={d.jobCode ?? d.code} />
                    <span>{d.jobName}</span>
                </span>
            },
            { title: t('Status'), className: 'td-datetime-sm', render: renderStatus },
        ],
        data: data,
        loading: requestSearch.isCompleted && invitationSearch.isCompleted,
        actions: [
          { icon: 'pi pi-trash', onClick: (r: any) => r.token === undefined ? deleteSubcontractorRequest(r) : restrictContractorInvitation(r) , disabled: (r: any) => checkRemove(r)}
        ]
    });

    return <div className='he c'>
        {canRender && dataTable()}
    </div>;
}
